import {
  ArrowRightIcon,
  Button,
  Card,
  Heading,
  Image,
  Pane,
  Paragraph,
  Spinner,
  Text,
  majorScale,
} from 'evergreen-ui'
import React, { FC } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import PodcastComingSoon from '../../components/PodcastComingSoon/PodcastComingSoon'
import PostBox from '../../components/PostBox/PostBox'
import { CONSTANTS } from '../../constants'
import {
  PODCAST_EPISODES,
  PODCAST_SHORTS,
  PREMIUM_CONTENT,
  PodcastEpisode,
  SIGNUP_URL,
  findEpisode,
} from '../../data/podcasts'
import { useAppSelector } from '../../hooks'
import { useGetPostsQuery } from '../../services/posts/posts.service'
import { Post, User } from '../../types'
import { generatePageTitle } from '../../utils/generate-page-title'
import { formatEpisodeDate, isPodcastPreviewUser } from '../../utils/podcast-preview'
import styles from './PodcastsPage.module.scss'

interface PodcastsPageProps {
  flex?: number
}

const LATEST_POSTS_COUNT = 3

const PodcastsPage: FC<PodcastsPageProps> = (props: PodcastsPageProps) => {
  const title = generatePageTitle(CONSTANTS.TITLES.PODCASTS)
  const navigate = useNavigate()

  const user: User | undefined = useAppSelector((state) => state.auth.user)
  const isPreviewUser = isPodcastPreviewUser(user)

  // latest community posts for the Community section
  const { data: posts, isLoading: isLoadingPosts } = useGetPostsQuery(
    {},
    { skip: !isPreviewUser },
  )
  const latestPosts: Post[] = [...(posts ?? [])]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, LATEST_POSTS_COUNT)

  const goToEpisode = (episode: PodcastEpisode) =>
    navigate(`${CONSTANTS.ROUTES.PODCASTS}/${episode.id}`)

  const episodeRow = (episode: PodcastEpisode, subtitle?: string) => (
    <Pane
      key={episode.id}
      display="flex"
      alignItems="center"
      padding={majorScale(1)}
      marginBottom={majorScale(1)}
      borderRadius="8px"
      border="default"
      cursor="pointer"
      className={styles.episodeRow}
      onClick={() => goToEpisode(episode)}
      data-testid={`PodcastEpisode-${episode.id}`}
    >
      <Image
        src={episode.thumbnailUrl}
        width={96}
        height={54}
        borderRadius="4px"
        marginRight={majorScale(2)}
        flexShrink={0}
        style={{ objectFit: 'cover' }}
      />
      <Pane minWidth={0}>
        <Heading size={400}>{episode.title}</Heading>
        <Text size={300} color="muted">
          {subtitle ?? `${formatEpisodeDate(episode.publishedAt)} · ${episode.durationMinutes} min`}
        </Text>
      </Pane>
    </Pane>
  )

  const communitySection = (
    <Card border="default" padding={majorScale(2)} background="white" marginBottom={majorScale(3)} data-testid="PodcastCommunity">
      <Pane display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={majorScale(1)}>
        <Pane>
          <Heading size={700}>Community</Heading>
          <Text size={400} color="muted">
            Discuss the podcast cases and share your own with colleagues.
          </Text>
        </Pane>
        <Button appearance="primary" iconAfter={ArrowRightIcon} onClick={() => navigate(CONSTANTS.ROUTES.COMMUNITY)}>
          Go to the Community
        </Button>
      </Pane>
      <Pane marginTop={majorScale(2)}>
        {isLoadingPosts ? (
          <Spinner size={24} />
        ) : latestPosts.length === 0 ? (
          <Text color="muted">No community posts yet.</Text>
        ) : (
          latestPosts.map((post) => (
            <PostBox
              key={post.id}
              post={post}
              isActive={false}
              onClick={(id: string) => navigate(`${CONSTANTS.ROUTES.COMMUNITY}/${id}`)}
            />
          ))
        )}
      </Pane>
    </Card>
  )

  const casesColumn = (
    <Card border="default" padding={majorScale(2)} background="white" className={styles.column} data-testid="PodcastCases">
      <Heading size={600} marginBottom={majorScale(2)}>
        Podcast Cases
      </Heading>
      {PODCAST_EPISODES.length === 0 ? (
        <Text color="muted">No episodes have been published yet.</Text>
      ) : (
        PODCAST_EPISODES.map((episode) => episodeRow(episode))
      )}
    </Card>
  )

  const shortsColumn = (
    <Card border="default" padding={majorScale(2)} background="white" className={styles.column} data-testid="PodcastShorts">
      <Heading size={600} marginBottom={majorScale(2)}>
        Shorts
      </Heading>
      {PODCAST_SHORTS.length === 0 ? (
        <Text color="muted">Short clips from each podcast case are coming soon.</Text>
      ) : (
        PODCAST_SHORTS.map((short) =>
          episodeRow(short, `From: ${findEpisode(short.caseId)?.title ?? 'podcast case'} · ${short.durationMinutes} min`),
        )
      )}
    </Card>
  )

  const premiumColumn = (
    <Card border="default" padding={majorScale(2)} background="white" className={styles.column} data-testid="PodcastPremium">
      <Heading size={600} marginBottom={majorScale(2)}>
        Premium
      </Heading>
      {PREMIUM_CONTENT.map((item) => (
        <Pane key={item.title} marginBottom={majorScale(2)}>
          {item.route ? (
            <Button appearance="minimal" paddingX={0} iconAfter={ArrowRightIcon} onClick={() => navigate(item.route!)}>
              <Heading size={400}>{item.title}</Heading>
            </Button>
          ) : (
            <Button appearance="minimal" paddingX={0} iconAfter={ArrowRightIcon} is="a" href={item.externalUrl} target="_blank" rel="noopener noreferrer">
              <Heading size={400}>{item.title}</Heading>
            </Button>
          )}
          <Paragraph size={300} color="muted">
            {item.description}
          </Paragraph>
        </Pane>
      ))}
      <Pane marginTop={majorScale(3)} paddingTop={majorScale(2)} borderTop="default">
        <Paragraph size={400} marginBottom={majorScale(1)}>
          Get full access to the video library and community.
        </Paragraph>
        <Button appearance="primary" width="100%" is="a" href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="PodcastSignupCta">
          Sign up for full access
        </Button>
      </Pane>
    </Card>
  )

  return (
    <Pane
      flex={props.flex ?? 1}
      className={styles.PodcastsPage}
      data-testid="PodcastsPage"
      padding={majorScale(4)}
      background="tint2"
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Heading size={800} marginBottom={majorScale(2)}>
        Podcasts
      </Heading>
      {!isPreviewUser ? (
        <Card border="default" padding={majorScale(2)} background="white">
          <PodcastComingSoon />
        </Card>
      ) : (
        <>
          {communitySection}
          <Pane display="flex" gap={majorScale(2)} flexWrap="wrap" alignItems="stretch">
            {casesColumn}
            {shortsColumn}
            {premiumColumn}
          </Pane>
        </>
      )}
    </Pane>
  )
}

export default PodcastsPage
